import { resolveFundingCommitmentMonths } from './resolveFundingCommitment';
import {
  resolveFacilitySize,
  resolveRequiredMaxExposure,
} from './resolvePlanCovenantLimits';
import { isRevSharePctConfigured } from './resolveProRataRevShare';
import { resolvePlanMonthLearningRate } from './hillPlanMonthLearning';
import { resolvePlanStartMonth } from './planStartMonth';
import { START_YEAR } from '../constants';
import { resolveMonthlyFeePct } from './xirrFee';

export const DEAL_IRR_LEARNING_FACTOR = 0.01;
export const DEAL_IRR_HILL_MISSING_MESSAGE =
  'No Hill function match. K and Max ROAS are empty. Enter values or use the ROAS vector tab.';

function toDecimalPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return 0;
  }
  return n > 1 ? n / 100 : n;
}

function optionalPositiveNumber(value) {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return n;
}

function optionalDecimalPct(value) {
  if (value == null || value === '') {
    return null;
  }
  return toDecimalPct(value);
}

export function resolvePlanFirstMarketingMonth(state) {
  const year = state?.planStartYear ?? START_YEAR;
  const month = resolvePlanStartMonth(state);
  return new Date(year, month, 1);
}

function coerceMarketingMonth(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), 1);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), 1);
    }
  }
  return null;
}

export function resolveFirstMarketingMonth(state) {
  const explicit = coerceMarketingMonth(state?.firstMarketingMonth);
  if (explicit) {
    return explicit;
  }
  return resolvePlanFirstMarketingMonth(state ?? {});
}

function resolveFirstMarketingMonthInternal(state) {
  return resolveFirstMarketingMonth(state);
}

export function resolveCogsDecimal(gpm) {
  const margin = Number(gpm);
  if (!Number.isFinite(margin)) {
    return 0;
  }
  return Math.max(0, 1 - margin / 100);
}

export const DEAL_IRR_COGS_TOOLTIP =
  'COGS as a percent of gross revenue. COGS is deducted from revenue first; repayments to Twelve are then calculated from net revenue after COGS, using the max revenue share %.';

export function resolveRevenueShareForRepaymentDecimal(state) {
  const explicit = state?.revenueShareForRepaymentPct;
  if (explicit != null && explicit !== '') {
    return toDecimalPct(explicit);
  }
  return resolveCogsDecimal(state?.gpm);
}

export function resolveDefaultFacilityFeeFirstPaymentMonth(_state) {
  return 0;
}

export function resolveFacilityFeeStartMonth(state) {
  if (state?.facilityFeeFirstPaymentMonth != null && state.facilityFeeFirstPaymentMonth !== '') {
    return Math.max(0, Math.floor(Number(state.facilityFeeFirstPaymentMonth) || 0));
  }
  return resolveDefaultFacilityFeeFirstPaymentMonth(state);
}

export function isHillModelMatched(hillModel) {
  if (!hillModel || !Number.isFinite(Number(hillModel.k)) || Number(hillModel.k) <= 0) {
    return false;
  }
  return (hillModel.maxRoasByMob || []).some((value) => Number(value) > 0);
}

export function resolveMarketingGraceMonths(state) {
  return Math.max(0, Math.min(1, Number(state?.marketingGracePeriod ?? 1)));
}

export function resolveDealIrrHillK(state, hillModel) {
  const override = optionalPositiveNumber(state?.dealIrrHillK);
  if (override != null) {
    return override;
  }
  if (!hillModel || !Number.isFinite(Number(hillModel.k))) {
    return null;
  }
  const k = Number(hillModel.k);
  return k > 0 ? k : null;
}

export function resolveDealIrrModelInputs(state, hillModel = null) {
  const marketing = (state?.marketing || []).map((value) => Number(value) || 0);
  const cumulativeRoas = (state?.roas || []).map((value) => toDecimalPct(value));
  const hillMatched = isHillModelMatched(hillModel);
  const resolvedK = resolveDealIrrHillK(state, hillModel);
  const grace = Math.max(0, Math.floor(Number(state?.gracePeriod) || 0));
  const globalMonthlyIrrPct = resolveMonthlyFeePct(state?.feeType, state?.feePct);
  const globalMonthlyIrr = globalMonthlyIrrPct / 100;
  const monthlyIrrByMonth = marketing.length
    ? marketing.map((_, index) => {
        const parsed = Number(state?.monthlyIrrPct?.[index]);
        if (Number.isFinite(parsed) && parsed > 0) {
          return parsed / 100;
        }
        return globalMonthlyIrr;
      })
    : null;
  const resolvedLearning = resolvePlanMonthLearningRate(state);
  const learning =
    state?.dealIrrLearningFactor != null && state.dealIrrLearningFactor !== ''
      ? resolvedLearning
      : resolvedLearning || DEAL_IRR_LEARNING_FACTOR;

  return {
    marketing,
    cumulativeRoas,
    commitmentMonths: resolveFundingCommitmentMonths(state),
    maxExposure: resolveRequiredMaxExposure(state?.maxExposure),
    maxMonthlyDraw: optionalPositiveNumber(state?.maxMonthlyDraw),
    fundingCap: toDecimalPct(state?.defaultFundingPct ?? 80),
    maxDrawIncrease: optionalDecimalPct(state?.maxDrawIncreasePct),
    cogs: resolveRevenueShareForRepaymentDecimal(state),
    grace,
    monthlyIrr: globalMonthlyIrr,
    monthlyIrrByMonth,
    facilityFeePct: Math.max(0, Number(state?.facilityFeePct) || 0) / 100,
    facilityFeeStart: resolveFacilityFeeStartMonth(state),
    facilityFeeInstallments: Math.max(
      1,
      Math.floor(Number(state?.facilityFeeInstallments) || 1)
    ),
    revShare: isRevSharePctConfigured(state?.defaultRevSharePct)
      ? toDecimalPct(state.defaultRevSharePct)
      : null,
    revShareByMonth: marketing.map((_, index) => {
      const stored = state?.revSharePct?.[index];
      return isRevSharePctConfigured(stored) ? toDecimalPct(stored) : null;
    }),
    facilitySize: resolveFacilitySize(state),
    learning,
    k: hillMatched && resolvedK != null ? resolvedK : null,
    maxRoasByMob: hillMatched
      ? hillModel.maxRoasByMob.map((value) => Number(value) || 0)
      : [],
    hillMatched,
    hillMessage: hillMatched ? '' : DEAL_IRR_HILL_MISSING_MESSAGE,
    firstMarketingMonth: resolveFirstMarketingMonthInternal(state),
    marketingGrace: resolveMarketingGraceMonths(state),
  };
}
